import { useInfiniteQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { nextConversationOffset } from '../lib/conversation-pagination';

export interface ConversationListItem {
  id: string;
  conversation_title?: string | null;
  topic: string;
  summary: string;
  site: string;
  estimated_tokens: number;
  turn_count: number;
  first_captured_at?: string;
  last_captured_at: string;
  private?: boolean;
  project_name?: string | null;
  author_name?: string;
  team_project_id?: string | null;
  team_project_name?: string | null;
}

export interface ConversationProject {
  id: string;
  name: string;
}

export interface Teammate {
  user_id: string;
  display_name: string;
  display_role: string;
}

export interface ConversationsSurfaceData {
  conversations: ConversationListItem[];
  total_count: number;
  projects: ConversationProject[];
  teammates?: Teammate[];
  counts_by_site?: Record<string, number>;
  counts_by_project?: Record<string, number>;
  counts_by_teammate?: Record<string, number>;
}

// Fixed infinite-scroll page size. The team conversations route clamps `limit` to 100 server-side,
// so the previous approach (grow one ever-larger `limit`) silently capped the list at 100 rows AND
// blanked `data` on every limit change — the query key churned with no placeholderData, so the list
// collapsed to empty and the page snapped to the top (bug-307). Offset paging with useInfiniteQuery
// avoids both: each request is a constant small page, and pages ACCUMULATE instead of refetching a
// bigger whole, so the rendered list only ever grows.
const PAGE_SIZE = 30;

export function useConversationsSurface(params?: {
  mode?: 'individual' | 'team';
  search?: string;
  site?: string;
  project_id?: string;
  team_project_id?: string;
  teammate_user_id?: string;
}) {
  const mode = params?.mode ?? 'individual';
  const endpoint = mode === 'team'
    ? '/api/surfaces/team/conversations'
    : '/api/surfaces/conversations';

  return useInfiniteQuery({
    // Key excludes the page offset so every page accumulates under one cache entry. Any filter change
    // (mode/search/site/…) changes the key and correctly restarts pagination from offset 0.
    queryKey: ['surfaces', mode === 'team' ? 'team-conversations' : 'conversations', params],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      if (params?.search) searchParams.set('search', params.search);
      if (params?.site) searchParams.set('site', params.site);
      if (params?.project_id) searchParams.set('project_id', params.project_id);
      if (params?.team_project_id) searchParams.set('team_project_id', params.team_project_id);
      if (params?.teammate_user_id) searchParams.set('teammate_user_id', params.teammate_user_id);
      searchParams.set('offset', String(pageParam));
      searchParams.set('limit', String(PAGE_SIZE));
      return apiFetch<ConversationsSurfaceData>(`${endpoint}?${searchParams.toString()}`);
    },
    getNextPageParam: (_lastPage, allPages) => nextConversationOffset(allPages),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
